import { decodeRedirectURL, isAllowedRedirectURL } from '@/helpers/redirect'

const PRODUCTION_HOSTNAME = 'mf-login.mercafacil.com'
const ZENDESK_HELP_CENTER_HOSTNAME = 'atendimento.mercafacil.com'
const REDIRECT_URL_KEY = 'redirect-url'
const TARGET_KEY = 'redirect-target'
const TARGET_DEFAULT_KEY = 'mercafacil'
const REDIRECT_QUERY_PARAM_KEYS = ['redirect-url', 'redirect_url', 'return_to']

class IntegrationService {
  getRedirectURL() {
    return JSON.parse(window.localStorage.getItem(REDIRECT_URL_KEY) || 'null')
  }

  hasRedirect() {
    return !!this.getRedirectURL()
  }

  getTarget() {
    return window.localStorage.getItem(TARGET_KEY) || TARGET_DEFAULT_KEY
  }

  isZendeskIntegration() {
    return this.getTarget() === 'zendesk'
  }

  isAllowedRedirect(redirectUrl) {
    return isAllowedRedirectURL(redirectUrl, { allowLocalhost: window.location.hostname !== PRODUCTION_HOSTNAME })
  }

  getZendeskAllowedDomains() {
    const domains = [ZENDESK_HELP_CENTER_HOSTNAME]
    try {
      domains.push(new URL(process.env.VUE_APP_ZENDESK_JWT_URL).hostname)
    } catch (error) {
      console.log('Error trying to get Zendesk JWT URL hostname', error)
    }
    return domains
  }

  isAllowedZendeskRedirect(redirectUrl) {
    return isAllowedRedirectURL(decodeRedirectURL(redirectUrl), { allowedDomains: this.getZendeskAllowedDomains() })
  }

  clearRedirectURL() {
    window.localStorage.removeItem(REDIRECT_URL_KEY)
  }

  storeValues(redirectUrl, target = TARGET_DEFAULT_KEY) {
    window.localStorage.setItem(REDIRECT_URL_KEY, JSON.stringify(redirectUrl))
    window.localStorage.setItem(TARGET_KEY, target)
  }

  resetStorage() {
    const oldRedirectReferrer = this.getRedirectURL()
    const oldTargetReferrer = this.getTarget()

    window.localStorage.clear()

    if (oldRedirectReferrer) {
      this.storeValues(oldRedirectReferrer, oldTargetReferrer)
    }
  }

  getQueryParams() {
    const result = {
      redirectUrl: null,
      shouldDirectRedirect: false,
    }

    if (window.location.href.indexOf('?') !== -1) {
      const queryStrings = window.location.href.slice(window.location.href.indexOf('?') + 1).split('&')

      for (const queryString of queryStrings) {
        const [key, value] = queryString.split('=')
        if (REDIRECT_QUERY_PARAM_KEYS.includes(key)) {
          result.redirectUrl = value
        } else if (key === 'redirect' && value === 'true') {
          result.shouldDirectRedirect = true
        }
      }
    }

    return result
  }

  getDefaultRedirectURL() {
    try {
      const { hostname } = window.location
      if (hostname.startsWith('mf-login-beta') || hostname.startsWith('localhost')) {
        return process.env.VUE_APP_DEFAULT_BETA_REDIRECT_URL
      }
    } catch (error) {
      console.log('Error trying to get default redirect URL', error)
    }
    return process.env.VUE_APP_DEFAULT_PROD_REDIRECT_URL || 'https://web.mercafacil.com/#/home/'
  }

  getIntegrationRedirectURL() {
    const redirectURL = this.getRedirectURL()

    if (this.isZendeskIntegration()) {
      return this.isAllowedZendeskRedirect(redirectURL) ? decodeRedirectURL(redirectURL) : process.env.VUE_APP_ZENDESK_REDIRECT_DEFAULT
    }

    return redirectURL && this.isAllowedRedirect(redirectURL) ? redirectURL : this.getDefaultRedirectURL()
  }

  redirectToIntegration(authCode) {
    const redirectURL = this.getIntegrationRedirectURL()

    if (this.isZendeskIntegration()) {
      const zendeskURL = process.env.VUE_APP_ZENDESK_JWT_URL
      window.location.href = `${zendeskURL}?return_to=${encodeURIComponent(redirectURL)}&jwt=${encodeURIComponent(authCode)}`
      return
    }

    if (redirectURL !== this.getRedirectURL()) {
      this.clearRedirectURL()
    }

    window.location.href = `${redirectURL}?auth_code=${authCode}`
  }
}

export default new IntegrationService()
